import isEqual from 'react-fast-compare';

export const countPixels = (title: string, fontSize: number): number => {
	const titleCanvas = document.getElementById('titleCanvas') as HTMLCanvasElement | null;

	if (!titleCanvas) {
		console.warn('Unable to find canvas to measure page title width.');
		return 0;
	}

	const ctx: CanvasRenderingContext2D | null = titleCanvas.getContext('2d');

	if (!ctx) {
		console.warn('Unable to get 2D drawing context for canvas.');
		return 0;
	}

	ctx.clearRect(0, 0, titleCanvas.width, titleCanvas.height);
	ctx.font = fontSize + 'px Arial';

	return ctx.measureText(title).width;
};

export const truncateText = (
	text: string,
	isPixelBased: boolean,
	isUrl: boolean,
	maxNumber: number,
	fontSize: number,
): string => {
	const dots = '...';
	let oldLength: number = text.length;
	if (isPixelBased) {
		if (!isUrl) {
			while (countPixels(text, fontSize) >= maxNumber || countPixels(text + dots, fontSize) >= maxNumber) {
				text = text.replace(/^\s+|\s+$/gm, '').replace(/\s(\S+)$/g, ''); // removes trailing spaces and the last word
				if (text.length >= oldLength) {
					// prevent infinite loop
					text = text.substring(0, text.length - 1);
				}
				oldLength = text.length;
			}
		} else {
			while (countPixels(text, fontSize) >= maxNumber || countPixels(text + dots, fontSize) >= maxNumber) {
				text = text.substring(0, text.length - 1);
			}
		}
	} else {
		while (text.length > maxNumber) {
			text = text.replace(/^\s+|\s+$/gm, '').replace(/\s(\S+)$/g, ''); // removes trailing spaces and the last word
			if (text.length >= oldLength) {
				// prevent infinite loop
				text = text.substring(0, maxNumber);
			}
			oldLength = text.length;
		}
	}
	return text + dots;
};

/**
 * Function that deep compares two objects, only checking whether keys that exist in both objects are equal
 */
export function isEqualCommonKeys(obj1: unknown, obj2: unknown, fieldsToIgnore: string[] = []): boolean {
	// The objects need to be the same type
	if (typeof obj1 !== typeof obj2) {
		return false;
	}

	//Don't need to iterate through the keys if the objects are already equal
	if (isEqual(obj1, obj2)) {
		return true;
	}

	// If these are arrays (possibly of objects with non-matching keys), check if their content is equal
	if (Array.isArray(obj1) && Array.isArray(obj2)) {
		if (obj1.length !== obj2.length) {
			return false;
		}
		obj1.sort();
		obj2.sort();

		for (let i = 0; i < obj1.length; i++) {
			if (!isEqualCommonKeys(obj1[i], obj2[i], fieldsToIgnore)) {
				return false;
			}
		}
		return true;
	}

	if (typeof obj1 === 'object' && typeof obj2 === 'object') {
		// null is an "object" but it has no properties
		if (obj1 === null || obj2 === null) {
			return obj1 === obj2;
		}

		// Only check if the keys that exist on both objects are equal
		const allKeys = new Set(Object.keys(obj1).concat(Object.keys(obj2)));
		for (const currKey of allKeys) {
			// Ignore some extra fields in the comparison
			if (fieldsToIgnore.includes(currKey)) {
				continue;
			}

			if (currKey in obj1 && currKey in obj2) {
				if (!isEqualCommonKeys(obj1[currKey], obj2[currKey], fieldsToIgnore)) {
					return false;
				}
			}
		}
		return true;
	}

	// The objects weren't arrays or objects, and they were not equal with a simple equality operator
	return false;
}

/**
 * Function that checks if a string is a valid absolute url
 */
export function isValidAbsoluteUrl(url: string) {
	const urlRegex = /^(?:(?:https?):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/;
	const result = url.match(urlRegex);
	return result !== null;
}

/**
 * Function that checks if a string is a valid relative url
 */
export function isValidRelativeUrl(url: string) {
	const urlRegex = /^(\/)(:[a-zA-Z0-9]*)?\/?([a-zA-Z0-9\-\._\?\,\'/\\\+&amp;%\$#\=~])*[^\.\,\)\(\s]$/gm;
	const result = url.match(urlRegex);
	return result !== null;
}

/**
 * Function that checks if a string is either a valid absolute or relative url
 */
export function IsValidAbsoluteOrRelativeUrl(url: string) {
	return isValidRelativeUrl(url) || isValidAbsoluteUrl(url);
}
